import type {
  DocumentMetadataUserEditValues,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { MetadataEditScope } from 'features/knowledgeDocumentViewer/types/metadataEditScope'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  draftLabelsToPageValues,
  DRAWING_NUMBER_FIELD_LABEL,
  isLeaveFamilyKeyChanged,
  isPageMetadataEditableLabel,
  lockedPageMetadataLabelsForScope,
  type PageMetadataDraftFields,
  SHEET_NUMBER_FIELD_LABEL,
  SIDEBAR_HIDDEN_PAGE_METADATA_LABELS,
  toEditablePageDraftFields,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'
import {
  getPageMetadataCardFields,
  type MetadataDisplayField,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataFields'
import {
  allowsCrossSurfaceMetadataCascade,
  formatCrossSurfaceCascadeWarning,
  listDocumentLabelsCascadingFromPageEdit,
} from 'features/knowledgeDocumentViewer/utils/sharedMetadataFieldBridge'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export type UsePageMetadataEditFormArgs = {
  pageEntry: SystemDocumentPageEntry | undefined
  pageNum: number
  userEditValues: DocumentPageMetadataUserEditValues | undefined
  userEditEntryId?: string
  editScope?: MetadataEditScope
  documentBaselineValues?: DocumentMetadataUserEditValues
  /** Used to gate cross-surface cascade warning (single-page only). */
  documentTotalPages?: number
  onSave: (values: DocumentPageMetadataUserEditValues) => Promise<void>
  onEditingChange?: (isEditing: boolean) => void
}

export type UsePageMetadataEditFormResult = {
  isEditing: boolean
  draftFields: PageMetadataDraftFields
  editableFields: MetadataDisplayField[]
  lockedLabels: ReadonlySet<string>
  showLeaveFamilyWarning: boolean
  crossSurfaceCascadeWarning: string | undefined
  beginEdit: () => void
  cancelEdit: () => void
  setFieldValue: (label: string, value: string) => void
  saveEdit: () => Promise<void>
}

export function usePageMetadataEditForm({
  pageEntry,
  pageNum,
  userEditValues,
  userEditEntryId,
  editScope,
  documentBaselineValues,
  documentTotalPages,
  onSave,
  onEditingChange,
}: UsePageMetadataEditFormArgs): UsePageMetadataEditFormResult {
  const [isEditing, setIsEditing] = useState(false)
  const [draftFields, setDraftFields] = useState<PageMetadataDraftFields>({})
  const [originalDrawingNumber, setOriginalDrawingNumber] = useState('')
  const [originalSheetNumber, setOriginalSheetNumber] = useState('')
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange

  const lockedLabels = useMemo(
    () => lockedPageMetadataLabelsForScope(editScope),
    [editScope]
  )

  const notifyEditingChange = useCallback((nextIsEditing: boolean) => {
    onEditingChangeRef.current?.(nextIsEditing)
  }, [])

  const clearEditState = useCallback(() => {
    setIsEditing(false)
    setDraftFields({})
    setOriginalDrawingNumber('')
    setOriginalSheetNumber('')
    notifyEditingChange(false)
  }, [notifyEditingChange])

  useEffect(() => {
    clearEditState()
  }, [pageEntry?.entry.id, pageNum, userEditEntryId, clearEditState])

  const beginEdit = useCallback(() => {
    const fields = getPageMetadataCardFields(
      pageEntry,
      pageNum,
      userEditValues
    ).map((field) => ({
      label: field.label,
      value: field.value ?? '',
    }))
    const draft = toEditablePageDraftFields(fields)
    setDraftFields(draft)
    setOriginalDrawingNumber(draft[DRAWING_NUMBER_FIELD_LABEL] ?? '')
    setOriginalSheetNumber(draft[SHEET_NUMBER_FIELD_LABEL] ?? '')
    setIsEditing(true)
    notifyEditingChange(true)
  }, [notifyEditingChange, pageEntry, pageNum, userEditValues])

  const cancelEdit = useCallback(() => {
    clearEditState()
  }, [clearEditState])

  const setFieldValue = useCallback((label: string, value: string) => {
    if (!isPageMetadataEditableLabel(label)) return
    setDraftFields((prev) => ({ ...prev, [label]: value }))
  }, [])

  const saveEdit = useCallback(async () => {
    const baseline = mergePageMetadataValues(
      pageEntry?.entry.data,
      userEditValues
    )
    const values = draftLabelsToPageValues(draftFields, baseline, lockedLabels)
    try {
      await onSave(values)
    } catch {
      // Keep edit mode open so the user can retry after a failed save.
      return
    }
    clearEditState()
  }, [
    clearEditState,
    draftFields,
    lockedLabels,
    onSave,
    pageEntry?.entry.data,
    userEditValues,
  ])

  const editableFields = getPageMetadataCardFields(
    pageEntry,
    pageNum,
    userEditValues
  )
    .filter((field) => !SIDEBAR_HIDDEN_PAGE_METADATA_LABELS.has(field.label))
    .map((field) => {
      const draftValue = isPageMetadataEditableLabel(field.label)
        ? draftFields[field.label]
        : undefined
      return {
        label: field.label,
        value: draftValue ?? field.value ?? '',
      }
    })

  // Only when the user explicitly chose "this document" from a real family.
  const showLeaveFamilyWarning =
    isEditing &&
    editScope === 'single' &&
    isLeaveFamilyKeyChanged({
      draftDrawingNumber: draftFields[DRAWING_NUMBER_FIELD_LABEL],
      originalDrawingNumber,
      draftSheetNumber: draftFields[SHEET_NUMBER_FIELD_LABEL],
      originalSheetNumber,
    })

  const crossSurfaceCascadeWarning = useMemo(() => {
    if (!isEditing || documentBaselineValues === undefined) return undefined
    if (!allowsCrossSurfaceMetadataCascade(documentTotalPages)) return undefined
    const pageBaseline = mergePageMetadataValues(
      pageEntry?.entry.data,
      userEditValues
    )
    const pageNext = draftLabelsToPageValues(
      draftFields,
      pageBaseline,
      lockedLabels
    )
    return formatCrossSurfaceCascadeWarning({
      counterpartSurface: 'document',
      labels: listDocumentLabelsCascadingFromPageEdit({
        pageBaseline,
        pageNext,
        documentBaseline: documentBaselineValues,
        requireAlignment: editScope !== 'family',
      }),
    })
  }, [
    documentBaselineValues,
    documentTotalPages,
    draftFields,
    editScope,
    isEditing,
    lockedLabels,
    pageEntry?.entry.data,
    userEditValues,
  ])

  return {
    isEditing,
    draftFields,
    editableFields,
    lockedLabels,
    showLeaveFamilyWarning,
    crossSurfaceCascadeWarning,
    beginEdit,
    cancelEdit,
    setFieldValue,
    saveEdit,
  }
}
